import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { TeamService } from '@/lib/api/services/teamService'
import { useWorkspaceContext } from '@/contexts/WorkspaceContext'
import { memberWorkspaceIds, spansAllWorkspaces } from '@/lib/workspaces/workspaceMembership'
import { isInactiveMember } from '@/lib/team/memberStatus'

export interface AgentDirectoryEntry {
  id: string
  user_id?: string | null
  name?: string | null
  email?: string | null
  role?: string | null
  status?: string | null
  has_all_conversations_access?: boolean
  account_permissions?: Record<string, boolean>
  menuPermissions?: string[] | null
  workspace_ids?: string[]
  spans_all_workspaces?: boolean
}

const parseAccessFlag = (v: any) =>
  v === 1 || v === true || v === '1' || v === 'true' || (typeof v === 'number' && v > 0)

const parseMenuPermissions = (m: Record<string, unknown>): string[] | null => {
  let prefs = m?.preferences as unknown
  if (typeof prefs === 'string') {
    try { prefs = JSON.parse(prefs) } catch { prefs = null }
  }
  const mp = (prefs as { menu_permissions?: unknown } | null)?.menu_permissions
    ?? (m as { menu_permissions?: unknown })?.menu_permissions
  return Array.isArray(mp) ? mp : null
}

export function buildAgentsById(res: unknown): Record<string, AgentDirectoryEntry> {
  const anyRes = res as any
  if (!anyRes || anyRes.error || anyRes.success === false) return {}
  let arr: any[] = []
  if (Array.isArray(anyRes?.data)) arr = anyRes.data
  else if (Array.isArray(anyRes?.data?.data)) arr = anyRes.data.data
  else if (anyRes?.data?.success && Array.isArray(anyRes?.data?.data)) arr = anyRes.data.data
  else if (anyRes?.success && Array.isArray(anyRes?.data)) arr = anyRes.data

  const map: Record<string, AgentDirectoryEntry> = {}
  for (const m of arr) {
    if (!m || !m.id) continue
    const accessRaw = (m as any).has_all_conversations_access ?? (m as any).hasAllConversationsAccess
    const hasAllAccess = parseAccessFlag(accessRaw)
    const accountPermissions: Record<string, boolean> = {}
    const assignedAccounts = (m as any).assigned_accounts
    if (Array.isArray(assignedAccounts)) {
      for (const acc of assignedAccounts) {
        if (!acc?.id) continue
        accountPermissions[acc.id] = parseAccessFlag(acc?.permissions?.has_all_conversations_access)
      }
    }
    map[m.id] = {
      id: m.id,
      user_id: m.user_id ?? m.userId ?? null,
      name: m.full_name || m.name || m.email || null,
      email: m.email || null,
      role: m.role ?? null,
      status: m.status ?? (isInactiveMember(m) ? 'inactive' : 'active'),
      has_all_conversations_access: hasAllAccess,
      account_permissions: accountPermissions,
      menuPermissions: parseMenuPermissions(m),
      workspace_ids: memberWorkspaceIds(m),
      spans_all_workspaces: spansAllWorkspaces(m),
    }
  }
  return map
}

const TEAM_DIRECTORY_QUERY_KEY = ['team-members-directory'] as const

async function fetchTeamDirectory(): Promise<Record<string, AgentDirectoryEntry>> {
  try {
    const rows = await TeamService.getAllTeamMembers()
    return buildAgentsById({ data: rows })
  } catch {
    return {}
  }
}

export function isInactiveAgent(agent: { status?: string | null } | null | undefined): boolean {
  if (!agent) return false
  return String(agent.status ?? 'active').trim().toLowerCase() === 'inactive'
}

export function useAgentsById(): Record<string, AgentDirectoryEntry> {
  const { data = {} } = useQuery({
    queryKey: TEAM_DIRECTORY_QUERY_KEY,
    queryFn: fetchTeamDirectory,
    staleTime: 60 * 1000,
    gcTime: 30 * 60 * 1000,
    refetchOnWindowFocus: true,
    retry: 1,
  })
  return data
}

export function expandAgentFilterIds(
  rawIds: string[],
  agentsById: Record<string, AgentDirectoryEntry>,
): string[] {
  const out: string[] = []
  for (const rawId of rawIds || []) {
    if (!rawId) continue
    if (rawId === '__unassigned__') { out.push(rawId); continue }
    out.push(rawId)
    const entry = agentsById[rawId]
      || Object.values(agentsById).find((a) => a.user_id && String(a.user_id) === rawId)
    const uid = entry?.user_id
    if (uid && uid !== rawId) out.push(String(uid))
    if (entry?.id && entry.id !== rawId) out.push(String(entry.id))
  }
  return Array.from(new Set(out))
}

export function useAssignableAgents(_businessAccountId?: string | null) {
  const { data: agentsById = {}, isLoading: loading } = useQuery({
    queryKey: TEAM_DIRECTORY_QUERY_KEY,
    queryFn: fetchTeamDirectory,
    staleTime: 60 * 1000,
    gcTime: 30 * 60 * 1000,
    refetchOnWindowFocus: true,
    retry: 1,
  })

  const { selectedWorkspaceId } = useWorkspaceContext()
  const workspaceAgents = useMemo(() => {
    const all = Object.values(agentsById)
    return all.filter((a) => {
      if (!selectedWorkspaceId || a.spans_all_workspaces) return true
      return (a.workspace_ids || []).includes(selectedWorkspaceId)
    })
  }, [agentsById, selectedWorkspaceId])

  const assignableAgents = useMemo(
    () => workspaceAgents.filter((a) => !isInactiveAgent(a)),
    [workspaceAgents],
  )

  const filterableAgents = useMemo(
    () => [...workspaceAgents].sort((a, b) => Number(isInactiveAgent(a)) - Number(isInactiveAgent(b))),
    [workspaceAgents],
  )

  return { agentsById, assignableAgents, filterableAgents, loading }
}
